import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { ExportButton } from '@/features/shared/ExportButton';
import { formatCurrencyCents, formatUsdCents } from '@/features/dashboard-finance/formatters';
import { useCustomers, type CustomerRow, type CustomersTab } from './useCustomers';
import type { ColumnDef } from '@/features/shared/types';

const TABS: CustomersTab[] = [
  'most-active',
  'top-volume',
  'top-revenue',
  'churn-risk',
  'zombie',
  'print-failures',
];

const KEY_TAB = 'tab';

const COLUMNS: ColumnDef<CustomerRow>[] = [
  { key: 'tenantName', header: 'Conta', accessor: (r) => r.tenantName },
  { key: 'countryCode', header: 'País', accessor: (r) => r.countryCode },
  {
    key: 'priceCents',
    header: 'Preço nativo',
    accessor: (r) => r.priceCents,
    csvFormatter: (r) => String(r.priceCents),
  },
  {
    key: 'usdEqCents',
    header: 'USD eq.',
    accessor: (r) => r.usdEqCents,
    csvFormatter: (r) => (r.usdEqCents / 100).toFixed(2),
  },
  { key: 'metric', header: 'Métrica', accessor: (r) => r.metricValue },
];

/**
 * Tela `/dashboard/customers`: seis abas de ranking de contas (mais ativas,
 * maior volume, maior receita, risco de churn, zumbis, falhas de impressão),
 * com exportação CSV e clique na linha para o detalhe do tenant.
 *
 * Origem: sem issue — commit fd8155bc4, 2026-05-04 (rota /dashboard/customers).
 *
 * Contrato: sem props. A aba mora na URL (`?tab=`); valor desconhecido cai em
 * `most-active`. A aba `print-failures` mostra aviso de instrumentação
 * pendente em vez de tabela. Clique na linha → `/tenants/{tenantId}`.
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/customers`, só super-admin).
 *
 * Arquitetura: só apresentação; toda leitura é do `useCustomers`. A
 * exportação usa o `ExportButton` compartilhado (`features/shared`).
 *
 * Dicas: os cabeçalhos de `COLUMNS` ("Conta", "País", "Preço nativo"…) estão
 * fixos em pt-BR, fora do i18n — achado registrado na #926.
 */
export function CustomersDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get(KEY_TAB) ?? 'most-active') as CustomersTab;
  const validTab: CustomersTab = TABS.includes(tab) ? tab : 'most-active';
  const state = useCustomers(validTab);

  function selectTab(next: CustomersTab): void {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        merged.set(KEY_TAB, next);
        return merged;
      },
      { replace: false },
    );
  }

  const rows = state.kind === 'READY' && !state.awaitingInstrumentation ? state.rows : [];

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('customers.subtitle')}>{t('customers.title')}</Header>}
    >
      <SpaceBetween size="l">
        <SpaceBetween direction="horizontal" size="xs">
          {TABS.map((tabId) => (
            <Button
              key={tabId}
              variant={tabId === validTab ? 'primary' : 'normal'}
              onClick={(): void => {
                selectTab(tabId);
              }}
            >
              {t(`customers.tab.${tabId}`)}
            </Button>
          ))}
        </SpaceBetween>

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && state.awaitingInstrumentation === true && (
          <Alert type="info">{t('customers.awaitingInstrumentation')}</Alert>
        )}
        {state.kind === 'READY' && !state.awaitingInstrumentation && (
          <Table<CustomerRow>
            items={rows}
            trackBy="tenantId"
            variant="container"
            header={
              <Header
                variant="h3"
                actions={
                  rows.length > 0 ? (
                    <ExportButton data={rows} columns={COLUMNS} source={`customers/${validTab}`} />
                  ) : undefined
                }
              >
                {t(`customers.tab.${validTab}`)}
              </Header>
            }
            empty={<Box textAlign="center" color="inherit">{t('customers.empty')}</Box>}
            columnDefinitions={[
              {
                id: 'account',
                header: t('customers.columns.account'),
                cell: (r): React.ReactNode => (
                  <Link
                    href={`/tenants/${r.tenantId}`}
                    onFollow={(e): void => {
                      e.preventDefault();
                      void navigate(`/tenants/${r.tenantId}`);
                    }}
                  >
                    {r.tenantName}
                  </Link>
                ),
              },
              {
                id: 'country',
                header: t('customers.columns.country'),
                cell: (r): React.ReactNode => r.countryCode,
              },
              {
                id: 'price',
                header: t('customers.columns.price'),
                cell: (r): React.ReactNode =>
                  r.currency ? formatCurrencyCents(r.priceCents, r.currency) : '—',
              },
              {
                id: 'usdEq',
                header: t('customers.columns.usdEq'),
                cell: (r): React.ReactNode => formatUsdCents(r.usdEqCents),
              },
              {
                id: 'metric',
                header: t(`customers.metric.${validTab}`),
                cell: (r): React.ReactNode => r.metricValue,
              },
            ]}
          />
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
